export const calculateRoundedIqdAmount = (
  amount: number,
  exchangeRate: number,
): number => {
  const numericAmount = Number(amount);
  const numericExchangeRate = Number(exchangeRate);

  if (numericAmount <= 0 || numericExchangeRate <= 0) {
    return 0;
  }

  return Math.ceil((numericAmount * numericExchangeRate) / 1000) * 1000;
};

/** Shape of the invoice fields the admin API serializes that carry the locked FX snapshot. */
export interface LockedInvoiceLike {
  isDeleted?: boolean;
  isCollected?: boolean;
  amount?: number;
  amountIQD?: number | null;
  lockedAmountIqd?: number | null;
  exchangeRate?: number | null;
  rateSource?: number | null;
  rateLockedAt?: string | null;
  collectionDate?: string | null;
  roundingRule?: string | null;
  invoiceNumber?: string | null;
}

export interface FxLockInfo {
  exchangeRate: number | null;
  rateSource: number | null;
  rateLockedAt: string | null;
  roundingRule: string | null;
  invoiceNumber: string | null;
}

export interface LockedNetPriceSummary {
  /** True when every non-deleted invoice on the order is collected and carries a locked IQD amount. */
  isFullyLocked: boolean;
  /** Immutable net price in USD (sum of locked invoice USD amounts). Null unless fully locked. */
  netPriceUsd: number | null;
  /** Immutable net price in IQD (sum of locked invoice IQD amounts). Null unless fully locked. */
  netPriceIqd: number | null;
  /** FX-lock metadata from the most recently settled invoice, for audit display. Null when none exists. */
  lock: FxLockInfo | null;
}

/**
 * Financial Control: derive the immutable net price from the locked invoice snapshot the admin
 * API already returns, instead of recomputing USD->IQD from the current live rate. Mirrors how
 * sar-customer reads locked values from the backend. Once every invoice on the order is paid, the
 * net price must equal the sum of the locked invoice amounts and never move when the rate changes.
 */
export const getLockedNetPriceSummary = (
  invoices: LockedInvoiceLike[] | null | undefined,
): LockedNetPriceSummary => {
  const empty: LockedNetPriceSummary = {
    isFullyLocked: false,
    netPriceUsd: null,
    netPriceIqd: null,
    lock: null,
  };

  if (!Array.isArray(invoices) || invoices.length === 0) {
    return empty;
  }

  const active = invoices.filter((i) => !i?.isDeleted);
  if (active.length === 0) {
    return empty;
  }

  const locked = active.filter(
    (i) => i?.isCollected && i?.lockedAmountIqd != null,
  );
  if (locked.length === 0) {
    return empty;
  }

  // Newest settled invoice supplies the audit metadata shown to the operator.
  const lockSource = [...locked].sort((a, b) => {
    const ta = new Date(a.rateLockedAt || a.collectionDate || 0).getTime();
    const tb = new Date(b.rateLockedAt || b.collectionDate || 0).getTime();
    return tb - ta;
  })[0];

  const lock: FxLockInfo = {
    exchangeRate: lockSource.exchangeRate ?? null,
    rateSource: lockSource.rateSource ?? null,
    rateLockedAt: lockSource.rateLockedAt ?? lockSource.collectionDate ?? null,
    roundingRule: lockSource.roundingRule ?? null,
    invoiceNumber: lockSource.invoiceNumber ?? null,
  };

  // Only when the whole order is paid do the locked invoices define the net price, matching the
  // backend which overrides priceInIQD to the locked sum only when all siblings are collected.
  const isFullyLocked = locked.length === active.length;
  if (!isFullyLocked) {
    return { isFullyLocked: false, netPriceUsd: null, netPriceIqd: null, lock };
  }

  const netPriceIqd = locked.reduce(
    (sum, i) => sum + Number(i.lockedAmountIqd || 0),
    0,
  );
  const netPriceUsd =
    Math.round(locked.reduce((sum, i) => sum + Number(i.amount || 0), 0) * 100) /
    100;

  return { isFullyLocked: true, netPriceUsd, netPriceIqd, lock };
};

/** Human-readable label for the persisted ExchangeRateSource enum (0 = Manual, 1 = API). */
export const formatRateSource = (source: number | null | undefined): string => {
  if (source === 0) return "Manual";
  if (source === 1) return "API";
  return "-";
};
